/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

import isObject from 'lodash/isObject';

import ScheduleEnum from './schedule-enum';
import ScheduleRandom from './schedule-random';
import ScheduleStatic from './schedule-static';

/**
 * Strategie přehrávání pro playlist.
 * Řízení přehrávání založené na zobrazení jednoho playlistu s libovolným typem.
 * Strategie automaticky zjišťuje jakého je playlist typu a dle toho plánuje.
 * @implements {ScheduleStrategy}
 */
class SchedulePlaylist {
  /**
   * Vytvoří instanci.
   * @param {ContentInfo} contentInfo Informace o obsahu zařízení.
   * @param {Playlist} playlist Playlist pro aplikaci strategie.
   * @param {ViewerRegistry} viewerRegistry Registr informací o typech médií.
   * @returns {SchedulePlaylist} Nová instance.
   */
  constructor(contentInfo, playlist, viewerRegistry) {
    if (!isObject(contentInfo?.store)) {
      throw new TypeError(`Invalid contentInfo ${contentInfo}.`);
    }
    if (!isObject(playlist)) {
      throw new TypeError(`Invalid playlist ${playlist}.`);
    }
    // Vyhledání stop playlistu.
    const trackArray = SchedulePlaylist.getTrackArray(contentInfo, playlist);
    // Nastavení vhodné strategie.
    this.scheduleStrategy = SchedulePlaylist.detectStrategy(
      contentInfo,
      playlist,
      trackArray,
      viewerRegistry
    );
  }

  /**
   * Zjistí seznam stopy playlistu z datového skladu.
   * @param {ContentInfo} contentInfo Informace o obsahu zařízení.
   * @param {Playlist} playlist Playlist pro výběr stop.
   * @private
   * @returns {Array.<Track>} Seznam stop.
   */
  static getTrackArray(contentInfo, playlist) {
    // TODO #530: Předělat na JSData Query Syntax.
    return contentInfo.store
      .filter('track')
      .filter((track) => track.parentPlaylistId === playlist.id);
  }

  /** @inheritdoc */
  destroy() {
    this.scheduleStrategy.destroy();
  }

  /**
   * Detekuje strategii pro playlist a vytvoří její instanci.
   * @param {ContentInfo} contentInfo Informace o obsahu zařízení.
   * @param {Playlist} playlist Playlist pro detekci strategie.
   * @param {Array.<Track>} trackArray Seznam stop playlistu pro detekci strategie.
   * @param {ViewerRegistry} viewerRegistry Registr informací o typech médií.
   * @private
   * @returns {ScheduleStrategy} Detekovaná strategie plánování.
   */
  static detectStrategy(contentInfo, playlist, trackArray, viewerRegistry) {
    // TODO #138 Zavést registry pro detekce strategií?
    // Z playlistu se stopami se podle scheduleEnum zjistí, kterou strategii použít.
    switch (playlist.scheduleEnum) {
      case ScheduleEnum.RANDOM:
        return new ScheduleRandom(
          contentInfo,
          playlist,
          trackArray,
          viewerRegistry
        );
      // Dynamické playlisty jsou zatím plánovány jako statické.
      case ScheduleEnum.DYNAMIC:
      case ScheduleEnum.STATIC:
        return new ScheduleStatic(
          contentInfo,
          playlist,
          trackArray,
          viewerRegistry
        );
      default:
        throw new Error(`Unable to detect strategy for playlist ${playlist}.`);
    }
  }

  /** @inheritdoc */
  go() {
    return this.scheduleStrategy.go();
  }

  /** @inheritdoc */
  off(...args) {
    this.scheduleStrategy.off(...args);
  }

  /** @inheritdoc */
  on(...args) {
    this.scheduleStrategy.on(...args);
  }

  /** @inheritdoc */
  plan(offset) {
    return this.scheduleStrategy.plan(offset);
  }

  /** @inheritdoc */
  skip() {
    this.scheduleStrategy.skip();
  }

  /** @inheritdoc */
  stop() {
    this.scheduleStrategy.stop();
  }

  /** @inheritdoc */
  toString() {
    return `SchedulePlaylist {playlist: ${this.playlist}}`;
  }
}

export default SchedulePlaylist;
